import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button component variants using class-variance-authority
 */
const buttonVariants = cva(
  // Base styles applied to all buttons
  [
    'inline-flex items-center justify-center gap-2',
    'whitespace-nowrap font-semibold',
    'transition-all duration-150 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        // Primary: ink background, white text (Save, New, Book)
        primary: [
          'bg-clovie-primary text-white',
          'hover:bg-clovie-primary-hover',
          'active:bg-clovie-primary-press',
        ],

        // Secondary: soft accent tint (chip colors), accent-dark text
        secondary: [
          'bg-clovie-chip text-clovie-chip-fg',
          'hover:bg-clovie-nav-active',
          'active:bg-clovie-nav-active',
        ],

        // Outline: White background, gray border
        // Hover: border + text go ink (primary), no background change
        outline: [
          'border border-border bg-background text-text-secondary',
          'hover:border-brand-ink hover:text-brand-ink',
          'active:border-brand-ink active:text-brand-ink',
        ],

        // Cancel: White background, neutral border, muted text (standard cancel button)
        cancel: [
          'border border-clovie-border-strong bg-white text-[#4B5563]',
          'hover:bg-[#F9FAFB] hover:text-[#374151]',
          'active:bg-[#F3F4F6] active:text-[#374151]',
        ],

        // Outline Primary: White background, accent border/text
        'outline-primary': [
          'border border-clovie-brand-soft bg-background text-clovie-brand-dark',
          'hover:border-clovie-brand hover:bg-clovie-hover',
          'active:bg-clovie-selected',
        ],

        // Ghost: Transparent background
        ghost: [
          'bg-transparent text-clovie-text',
          'hover:bg-accent-teal-light hover:text-text-primary',
          'active:bg-accent-teal/20',
        ],

        // Action: solid CTA, same ink as primary
        action: [
          'bg-clovie-primary text-white',
          'hover:bg-clovie-primary-hover',
          'active:bg-clovie-primary-press',
        ],

        // Dark: Dark navy background
        dark: [
          'bg-[#1F2937] text-white border border-[#374151]',
          'hover:bg-[#374151]',
          'active:bg-[#1F2937]',
        ],

        // Link: Text only, looks like a link
        link: ['text-link underline-offset-4', 'hover:underline', 'active:text-link/80'],

        // Destructive: Error/danger actions
        destructive: [
          'bg-destructive text-destructive-foreground',
          'hover:bg-destructive/90',
          'active:bg-destructive',
        ],
      },

      size: {
        default: 'h-10 px-6 py-2 text-sm',
        sm: 'h-8 px-4 py-1.5 text-xs',
        lg: 'h-12 px-8 py-3 text-base',
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-8 w-8 p-0',
        'icon-lg': 'h-12 w-12 p-0',
      },

      shape: {
        default: 'rounded-sm', // 4px border-radius
        rounded: 'rounded-md', // 6px border-radius
        pill: 'rounded-full', // Fully rounded (ADD CLIENT style)
      },

      // Full width option
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },

    defaultVariants: {
      variant: 'primary',
      size: 'default',
      shape: 'default',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /**
   * If true, the button will be rendered as a child component (Slot)
   * Useful for rendering as links with button styling
   */
  asChild?: boolean;

  /**
   * Loading state - shows spinner and disables button
   */
  loading?: boolean;

  /**
   * Icon to display before the button text
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display after the button text
   */
  rightIcon?: React.ReactNode;
}

/**
 * Button component with multiple variants, sizes, and shapes
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      fullWidth,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Slot merges its props into its one child; wrapping that child in a fragment (below) would drop them.
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, shape, fullWidth, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, shape, fullWidth, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner className="animate-spin" />
            <span className="sr-only">Loading...</span>
            {children}
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    );
  }
);
Button.displayName = 'Button';

/**
 * Simple loading spinner SVG
 */
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export { Button, buttonVariants };
